import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { api } from '../lib/api';
import type { ApiError } from '../lib/api';
import { useQuery } from './DataProvider';
import type { ModuleName, Me } from '../lib/types';

interface SessionCtx {
  me: Me | null;
  loading: boolean;
  /**
   * Why the registry could not be read, when it could not be.
   *
   * This is NOT the same thing as `can()` returning false, and conflating the
   * two is how an outage gets reported as a permissions problem: if `me()`
   * fails, every module denies, and the user is told to ask an administrator
   * for access they already have. A caller must check this first.
   */
  error: ApiError | null;
  /** Retry the identity fetch alone. */
  reload: () => void;
  /** Deny by default: no session, no module. */
  can: (module: ModuleName) => boolean;
}

const Ctx = createContext<SessionCtx>({
  me: null, loading: true, error: null, reload: () => {}, can: () => false,
});

/**
 * The signed-in user, fetched once and shared. Screens use `can()` rather
 * than checking `isAdmin` themselves — the registry decides, not the UI.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const { data, loading, error, reload } = useQuery('me', (o) => api.me(o));

  const value: SessionCtx = {
    me: data,
    loading,
    error,
    reload,
    can: (module) => Boolean(data?.isAdmin) || Boolean(data?.modules.includes(module)),
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useMe(): SessionCtx {
  return useContext(Ctx);
}
